/* eslint-disable max-lines */
import {
  chargeElavonCard,
  chargeElavonOnline,
  chargeSavedCard,
  getElavonOnlineCredentials
} from 'api/Transaction';

import { GENERIC_ERROR_SUPPORT } from 'lib/constants/messages';

import { showAlert } from 'components/shared/Alert/Alert';

const DEFAULT_ERROR_MESSAGE = GENERIC_ERROR_SUPPORT;
const PAYMENT_SCRIPT_UNAVAILABLE_MESSAGE =
  'Payment service is still loading. Please try again in a moment.';
const DECLINED_MESSAGE = 'Payment was declined.';
const CANCELLED_MESSAGE = 'Payment canceled.';

const cancelledError = () => Object.assign(new Error(CANCELLED_MESSAGE), { cancelled: true });

export const elavonPreAuthDeferredHandler = async (
  navigate,
  { billingAddress, invoiceToken, surchargeAmount, payerId, patientId, terminalId, preAuthForm }
) => {
  const credentialsParams = {
    amount: 0,
    saveCard: true,
    tokenOnly: true,
    billingAddress,
    invoiceToken,
    surcharge: surchargeAmount,
    payerId,
    terminalId
  };
  if (patientId) credentialsParams.patientId = patientId;
  const credentialsResponse = await getElavonOnlineCredentials(navigate, credentialsParams);
  const credentialsData = credentialsResponse?.data || credentialsResponse;
  if (credentialsData?.code !== 0 || !credentialsData?.token) {
    throw new Error(credentialsData?.error || DEFAULT_ERROR_MESSAGE);
  }

  return new Promise((resolve, reject) => {
    if (!window.PayWithConverge?.open) {
      reject(new Error(PAYMENT_SCRIPT_UNAVAILABLE_MESSAGE));
      return;
    }

    try {
      window.PayWithConverge.open(
        { ssl_txn_auth_token: credentialsData.token },
        {
          onError: (err) => reject(new Error(err?.errorMessage || DEFAULT_ERROR_MESSAGE)),
          onCancelled: () => reject(cancelledError()),
          onDeclined: () => reject(new Error(DECLINED_MESSAGE)),
          onApproval: (response) => {
            resolve({
              code: 0,
              deferred: true,
              processor: 'elavon',
              terminalId,
              preAuthForm,
              onlineCard: { ...response, terminal_id: terminalId }
            });
          }
        }
      );
    } catch (error) {
      reject(new Error(error?.message || DEFAULT_ERROR_MESSAGE));
    }
  });
};

export const elavonSavedCardHandler = (
  navigate,
  {
    invoices,
    appointmentId,
    surchargeAmount,
    gratuity,
    amount,
    appliedBalance,
    invoiceToken,
    methodDetail,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId
  }
) => {
  if (!methodDetail) return;

  return chargeSavedCard(navigate, {
    amount,
    invoiceToken,
    invoices,
    appointmentId,
    cardId: methodDetail,
    surchargeAmount,
    gratuity,
    appliedBalance,
    ...(recurringPayment && { recurringPayment }),
    ...(planId && { planId }),
    ...(updatePlanId && { updatePlanId }),
    ...(scheduleId && { scheduleId })
  });
};

const completeTransaction = async (
  navigate,
  {
    token,
    invoices,
    invoiceToken,
    transactionId,
    response,
    payerId,
    appliedBalance,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    createPatientFromCCInfo,
    terminalId,
    topsOption,
    preAuthForm,
    tempCard,
    silent = false
  }
) => {
  try {
    const cardHolderName = createPatientFromCCInfo
      ? [response?.ssl_first_name, response?.ssl_last_name].filter(Boolean).join(' ').trim()
      : undefined;

    const res = await chargeElavonOnline(navigate, {
      token,
      invoices,
      invoiceToken,
      transactionId,
      transaction: response,
      payerId,
      appliedBalance,
      ...(terminalId && { terminalId }),
      ...(topsOption && { topsOption }),
      ...(preAuthForm && { preAuthForm }),
      ...(tempCard && { tempCard }),
      ...(recurringPayment && { recurringPayment }),
      ...(planId && { planId }),
      ...(updatePlanId && { updatePlanId }),
      ...(scheduleId && { scheduleId }),
      ...(createPatientFromCCInfo &&
        cardHolderName && { createPatientFromCCInfo: true, cardHolderName })
    });

    const { code, redirect, error, cardId: _cardId, recurringError } = res;
    switch (code) {
      case -1:
        navigate(redirect);
        break;
      case 0: {
        if (recurringError) {
          showAlert({
            title:
              "Payment processed, but the recurring plan couldn't be set up. Please contact support.",
            color: 'warning'
          });
        }
        return res;
      }
      default:
        if (silent) return res;
        showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
        break;
    }
  } catch (error) {
    console.error(error);
    if (silent) return null;
    showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
  }
};

const openLightbox = async (
  navigate,
  token,
  transactionId,
  {
    invoices,
    invoiceToken,
    payerId,
    appliedBalance,
    tokenOnly,
    preAuthForm,
    terminalId,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    patientId,
    createPatientFromCCInfo,
    topsOption,
    tempCard
  }
) => {
  return new Promise((resolve, reject) => {
    try {
      const callback = {
        async onError(error) {
          try {
            if (tokenOnly) {
              reject(new Error(error?.errorMessage || DEFAULT_ERROR_MESSAGE));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              silent: true,
              response: { txnStatus: 'error', ...error }
            });
            reject(new Error(error?.errorMessage || result?.error || DEFAULT_ERROR_MESSAGE));
          } catch (err) {
            reject(new Error(err?.message || DEFAULT_ERROR_MESSAGE));
          }
        },
        async onCancelled() {
          try {
            if (tokenOnly) {
              reject(cancelledError());
              return;
            }

            await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              silent: true,
              response: { txnStatus: 'cancelled' }
            });
            reject(cancelledError());
          } catch {
            reject(cancelledError());
          }
        },
        async onDeclined(response) {
          try {
            if (tokenOnly) {
              reject(new Error(DECLINED_MESSAGE));
              return;
            }

            await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              silent: true,
              response: { txnStatus: 'declined', ...response }
            });
            reject(new Error(DECLINED_MESSAGE));
          } catch {
            reject(new Error(DECLINED_MESSAGE));
          }
        },
        async onApproval(response) {
          try {
            if (tokenOnly) {
              const result = await chargeElavonCard(navigate, {
                targetUserId: patientId,
                terminalId,
                tokenOnly,
                invoiceToken,
                payerId,
                onlineCard: { ...response, terminal_id: terminalId },
                preAuthForm,
                ...(recurringPayment && { recurringPayment }),
                ...(planId && { planId }),
                ...(updatePlanId && { updatePlanId }),
                ...(scheduleId && { scheduleId })
              });
              resolve(result);
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              terminalId,
              recurringPayment,
              planId,
              updatePlanId,
              scheduleId,
              createPatientFromCCInfo,
              topsOption,
              preAuthForm,
              tempCard,
              response: { txnStatus: 'approved', ...response }
            });
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }
      };
      if (!window.PayWithConverge?.open) {
        reject(new Error(PAYMENT_SCRIPT_UNAVAILABLE_MESSAGE));
        return;
      }

      window.PayWithConverge.open({ ssl_txn_auth_token: token }, callback);
    } catch (error) {
      console.error(error);
      reject(new Error(error?.message || DEFAULT_ERROR_MESSAGE));
    }
  });
};

export const chargeElavonOnlineHandler = async (
  navigate,
  {
    amount,
    saveCard,
    tokenOnly,
    billingAddress,
    invoiceToken,
    surchargeAmount,
    gratuity,
    payerId,
    patientId,
    appliedBalance,
    invoices,
    transactionId,
    preAuthForm,
    terminalId,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    createPatientFromCCInfo,
    topsOption,
    tempCard,
    transactionTags
  }
) => {
  const isInstallmentsNoUpfront =
    recurringPayment?.type === 'installments' && !(Number(recurringPayment?.upfrontPayment) > 0);
  const effectiveTokenOnly = tokenOnly || isInstallmentsNoUpfront;
  const effectiveAmount = isInstallmentsNoUpfront ? 0 : amount;

  try {
    const credentialsResponse = await getElavonOnlineCredentials(navigate, {
      amount: effectiveAmount,
      saveCard: saveCard || !!recurringPayment || !!tempCard,
      tokenOnly: effectiveTokenOnly,
      billingAddress,
      invoiceToken,
      surcharge: surchargeAmount,
      gratuity,
      payerId,
      appliedBalance,
      patientId,
      terminalId,
      transactionTags,
      ...(recurringPayment && { recurringPayment })
    });
    const credentialsData = credentialsResponse?.data || credentialsResponse;

    if (credentialsData?.code === 0) {
      const finalTransactionId = credentialsData.transactionId || transactionId;
      return openLightbox(navigate, credentialsData.token, finalTransactionId, {
        invoices,
        invoiceToken,
        payerId,
        appliedBalance,
        tokenOnly: effectiveTokenOnly,
        saveCard: saveCard || !!recurringPayment || !!tempCard,
        preAuthForm,
        terminalId,
        recurringPayment,
        planId,
        updatePlanId,
        scheduleId,
        patientId,
        createPatientFromCCInfo,
        topsOption,
        tempCard
      });
    }

    if (credentialsData?.code === 6) {
      throw new Error(
        'Recurring payments require saving the card on file. Please enable card saving and try again.'
      );
    }

    throw new Error(credentialsData?.error || DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    console.error('Error in chargeElavonOnlineHandler:', error);
    throw error?.cancelled ? error : new Error(error?.message || DEFAULT_ERROR_MESSAGE);
  }
};
